import { Package } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import type { ResolvedPackageItem } from "../packages.catalog";

/**
 * El arte del paquete, arriba de la tarjeta (Anahí, 28-ago: "las
 * imágenes de los paquetes es lo que falta").
 *
 * - Con `imageUrl`: la imagen a todo el ancho, como en producción, donde
 *   cada membresía trae su banner (Membresía 1/2/3).
 * - Sin ella: el mosaico de los productos que trae, sobre el panel
 *   lavanda del resumen. Un paquete recién creado en el panel, sin arte
 *   todavía, sigue enseñando QUÉ trae en vez de un hueco gris.
 * - Sin productos resueltos (el catálogo aún viaja): el icono, sin saltos.
 */
export function PackageArtwork({
  imageUrl,
  name,
  items,
}: {
  imageUrl: string | undefined;
  name: string;
  items: ReadonlyArray<ResolvedPackageItem>;
}) {
  if (imageUrl) {
    return (
      <div className="aspect-[3/2] w-full overflow-hidden bg-ds-lavender-bg">
        {/* `object-top` y 3:2: el arte de producción (Membresía 1/2/3) lleva el
            título arriba y, al pie, una franja con el PRECIO IMPRESO de la
            membresía vieja ($500 / $899 / $1,199), que no es el de estos
            paquetes. El recorte deja fuera esa franja: el único precio que
            se ve es el que calcula el servidor, abajo en la tarjeta. */}
        <img src={imageUrl} alt={name} className="h-full w-full object-cover object-top" loading="lazy" />
      </div>
    );
  }

  const tiles = items.filter((item) => item.product?.imageUrl).slice(0, 4);
  const tilt = ["-rotate-3", "rotate-2", "rotate-3", "-rotate-2"];

  return (
    <div
      className="flex aspect-[16/10] w-full items-center justify-center bg-gradient-to-br from-ds-lavender-bg via-[#E6E1F4] to-ds-lavender-bg-2 p-5"
      data-testid="package-mosaic"
    >
      {tiles.length === 0 ? (
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/80 text-ds-violet-pop shadow-ds-xs">
          <Package className="h-6 w-6" aria-hidden="true" />
        </span>
      ) : (
        <ul
          className={cn(
            "grid h-full items-center gap-3",
            tiles.length === 1 ? "grid-cols-1" : tiles.length === 3 ? "grid-cols-3" : "grid-cols-2",
          )}
        >
          {tiles.map((item, index) => (
            <li
              key={item.productId}
              className={cn(
                "relative aspect-square h-full max-h-[120px] overflow-hidden rounded-2xl border border-white bg-white shadow-ds-card",
                tiles.length === 4 && "max-h-[84px]",
                tiles.length === 3 && "max-h-[96px]",
                tilt[index % tilt.length],
              )}
            >
              <img
                src={item.product!.imageUrl!}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
              {item.quantity > 1 && (
                <span className="absolute right-1 top-1 rounded-full bg-ds-ink/85 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  ×{item.quantity}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
